import type { ReactNode } from "react";
import { useRef } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { Button } from "@similarweb/ui-components";
import { Text } from "@similarweb/ui-components/foundations";

import { useTranslation } from "pro-features/components/WithTranslation";
import { SimpleStickySection } from "app/pages/sales-intelligence/sub-modules/account-overview/shared/components/StickyTabs/SimpleStickySection";
import { StickyTabsWithoutCollapse } from "app/pages/sales-intelligence/sub-modules/account-overview/shared/components/StickyTabs/StickyTabsWithoutCollapse";

import useJobStickyTabs from "../../../hooks/useJobStickyTabs";
import type { CrmEnrichmentJobTabName } from "../../../types/types";
import { StyledFooterWrapper } from "../../styles";
import JobEditorSection from "../JobEditorSection/JobEditorSection";

import {
    StyledCrmEnrichmentJobEditorDeleteWrapper,
    StyledJobEditorButtonsWrapper,
    StyledJobEditorContent,
    StyledJobEditorScrolledContainer,
    StyledJobEditorWrapper,
} from "./styles";
type JobEditorContentConfig = Record<CrmEnrichmentJobTabName, ReactNode | undefined>;

type Props = {
    isSaveButtonDisabled: boolean;
    isLoading: boolean;
    content: JobEditorContentConfig;
    onSave: VoidFunction;
    onCancel: VoidFunction;
    onDeleteClick: VoidFunction;
    onTabClick?: (tabName: CrmEnrichmentJobTabName) => void;
};

const JobEditor = ({
    isSaveButtonDisabled,
    isLoading,
    onSave,
    onCancel,
    onDeleteClick,
    onTabClick,
    content,
}: Props) => {
    const translate = useTranslation();

    const pageRef = useRef<HTMLDivElement>(null);

    const { activeTab, onTabClick: onStickyTabClick, tabsConfig, tabsRefs, onIntersection } = useJobStickyTabs();

    const handleTabClick = (tabName: CrmEnrichmentJobTabName): void => {
        onTabClick?.(tabName);
        onStickyTabClick(tabName);
    };

    return (
        <StyledJobEditorWrapper data-automation="crm-enrichment-job-editor">
            <StyledJobEditorContent>
                <Text
                    fontVariant="h4"
                    color={colorsPalettes.carbon[500]}
                >
                    {translate("si.crm-enrichment.job-editor.title")}
                </Text>

                <StyledJobEditorScrolledContainer ref={pageRef}>
                    <StickyTabsWithoutCollapse
                        activeTab={activeTab}
                        viewportElement={pageRef.current}
                        tabsConfig={tabsConfig}
                        tabRefs={Object.values(tabsRefs)}
                        onTabClick={handleTabClick}
                    />

                    <SimpleStickySection
                        name="type"
                        stickySectionRef={tabsRefs.type}
                        onIntersection={onIntersection}
                    >
                        <JobEditorSection titleKey="si.crm-enrichment.job-wizard.legend.type">
                            {content.type}
                        </JobEditorSection>
                    </SimpleStickySection>

                    <SimpleStickySection
                        name="settings"
                        stickySectionRef={tabsRefs.settings}
                        onIntersection={onIntersection}
                    >
                        <JobEditorSection titleKey="si.crm-enrichment.job-wizard.legend.settings">
                            {content.settings}
                        </JobEditorSection>
                    </SimpleStickySection>

                    <SimpleStickySection
                        name="filters"
                        stickySectionRef={tabsRefs.filters}
                        onIntersection={onIntersection}
                    >
                        <JobEditorSection titleKey="si.crm-enrichment.job-wizard.legend.filters">
                            {content.filters}
                        </JobEditorSection>
                    </SimpleStickySection>

                    <SimpleStickySection
                        name="fields-mapping"
                        stickySectionRef={tabsRefs["fields-mapping"]}
                        onIntersection={onIntersection}
                    >
                        <JobEditorSection titleKey="si.crm-enrichment.job-wizard.legend.fields-mapping">
                            {content["fields-mapping"]}
                        </JobEditorSection>
                    </SimpleStickySection>

                    <JobEditorSection
                        titleKey="si.crm-enrichment.job-settings.delete.title"
                        isUnderlined={false}
                    >
                        <StyledCrmEnrichmentJobEditorDeleteWrapper>
                            <Text
                                fontVariant="body2"
                                color={colorsPalettes.carbon[400]}
                            >
                                {translate("si.crm-enrichment.job-editor.delete.text")}
                            </Text>

                            <Button
                                type="flatWarning"
                                onClick={onDeleteClick}
                            >
                                {translate("si.crm-enrichment.job-editor.delete.button")}
                            </Button>
                        </StyledCrmEnrichmentJobEditorDeleteWrapper>
                    </JobEditorSection>
                </StyledJobEditorScrolledContainer>
            </StyledJobEditorContent>

            <StyledFooterWrapper>
                <StyledJobEditorButtonsWrapper>
                    <Button
                        type="flat"
                        onClick={onCancel}
                    >
                        {translate("si.common.button.cancel")}
                    </Button>

                    <Button
                        isDisabled={isSaveButtonDisabled}
                        isLoading={isLoading}
                        onClick={onSave}
                    >
                        {translate("si.common.button.save")}
                    </Button>
                </StyledJobEditorButtonsWrapper>
            </StyledFooterWrapper>
        </StyledJobEditorWrapper>
    );
};

export default JobEditor;
